import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase, CheckIn } from '../lib/supabase';
import { useEmberStore } from '../lib/store';
import { Flame, Plus, ArrowLeft } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import CheckInCard from '../components/CheckInCard';
import { SkeletonCheckInCard } from '../components/SkeletonLoader';
import Button from '../components/ui/Button';

export default function CheckIns() {
  const { currentUser, checkIns, setCheckIns } = useEmberStore();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCheckIns();
  }, []);

  // Scroll to a specific check-in if hash present
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#checkin-')) {
      const el = document.querySelector(hash) as HTMLElement | null;
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
      }
    }
  }, [loading]);

  async function loadCheckIns() {
    try {
      const { data, error } = await supabase
        .from('checkins')
        .select(`
          *,
          tobacco:tobaccos(*),
          venue:venues(*),
          cellar_tobacco:cellar_tobacco(*),
          cellar_pipe:cellar_pipes(*),
          cellar_lighter:cellar_lighters(*),
          profile:profiles!checkins_user_id_fkey(username, is_profile_public)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) setCheckIns(data as any);
    } catch (err) {
      console.error('Error loading check-ins:', err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '80px' }}>
      {/* Header */}
      <header style={{
        padding: '1rem',
        borderBottom: '1px solid #1f1f1f',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        backgroundColor: '#0b0b0b',
        zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Link to="/" style={{ color: 'var(--color-accent)', textDecoration: 'none' }}>
            <ArrowLeft size={24} />
          </Link>
          <Flame size={32} color="var(--color-accent)" />
          <h1 style={{ fontSize: '1.5rem', letterSpacing: '0.02em', textTransform: 'uppercase' }}>Check-ins</h1>
        </div>
        <Link to="/checkin">
          <button style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            backgroundColor: 'var(--color-secondary)',
            color: '#ffffff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '0.875rem'
          }}>
            <Plus size={16} />
            Add New
          </button>
        </Link>
      </header>

      {/* Check-ins List */}
      <div className="container" style={{ paddingTop: '1rem' }}>
        {loading ? (
          <>
            <SkeletonCheckInCard />
            <SkeletonCheckInCard />
            <SkeletonCheckInCard />
          </>
        ) : checkIns.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: '#d3de5e', marginBottom: '1rem' }}>No check-ins yet.</p>
            <Link to="/checkin">
              <Button variant="primary">
                Create Your First Check-in
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={{ color: '#999', fontSize: '0.875rem' }}>
                {checkIns.length} check-in{checkIns.length !== 1 ? 's' : ''}
              </p>
            </div>
            
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '1rem',
              alignItems: 'center'
            }}>
              {checkIns.map((checkIn) => (
                <CheckInCard 
                  key={checkIn.id} 
                  checkIn={checkIn}
                  onDelete={() => loadCheckIns()}
                  enablePhotoFocus
                />
              ))}
            </div>
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
